export interface FieldError {
  field: string;
  message: string;
}

export type AddressForm = {
  address1: string;
  address2: string;
  city: string;
  stateCd: string;
  zip: string;
  countryCd: string;
};
export const EMPTY_ADDRESS: AddressForm = {
  address1: "",
  address2: "",
  city: "",
  stateCd: "",
  zip: "",
  countryCd: "US",
};

export type VolumeDetailsForms = {
  averageMonthlyVolume: string;
  highTicketAmount: string;
  averageTicketAmount: string;
};

export const EMPTY_VOLUME: VolumeDetailsForms = {
  averageMonthlyVolume: "0",
  highTicketAmount: "0",
  averageTicketAmount: "0",
};

export type BusinessForm = {
  businessName: string;
  dbaName: string;
  taxFilingName: string;
  taxFilingMethod: string;
  taxID: string;
  businessStartDate: string;
  mcc: string;
  storeType: string;
  businessDescription: string;
  website: string;
  contactEmail: string;
  contactPhone: string;
};
export const EMPTY_BUSINESS: BusinessForm = {
  businessName: "",
  dbaName: "",
  taxFilingName: "",
  businessStartDate: "",
  taxFilingMethod: "EIN",
  taxID: "",
  mcc: "",
  storeType: "",
  businessDescription: "",
  website: "",
  contactEmail: "",
  contactPhone: "",
};

export type OwnerForm = {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  ssn: string;
  dateOfBirth: string;
  ownershipPct: string;
  ownershipTypeCd: string;
  driversLicenseNumber: string;
  driversLicenseStateCd: string;
};
export const EMPTY_OWNER: OwnerForm = {
  firstName: "",
  lastName: "",
  title: "PRESIDENT",
  email: "",
  phone: "",
  ssn: "",
  dateOfBirth: "",
  ownershipPct: "100",
  ownershipTypeCd: "PUBCORP",
  driversLicenseNumber: "",
  driversLicenseStateCd: "",
};

export type BankForm = {
  accountNumber: string;
  // Double-entry confirmation of accountNumber (issue #195). UI-only: it is
  // never sent to the wire (payload builders pick fields explicitly); the
  // shared validator blocks submit until it agrees with accountNumber.
  confirmAccountNumber: string;
  routingNumber: string;
  accountType: string;
  bankName: string;
};
export const EMPTY_BANK: BankForm = {
  accountNumber: "",
  confirmAccountNumber: "",
  routingNumber: "",
  accountType: "BIZ",
  bankName: "",
};

export type ModeForm = {
  eCommercePct: string;
  keyedPct: string;
  mailOrderPct: string;
  swipedPct: string;
};
export const EMPTY_MODE: ModeForm = {
  eCommercePct: "0",
  keyedPct: "0",
  mailOrderPct: "0",
  swipedPct: "100",
};
// Shape of a patch()-produced per-field setter, for typing the shared
// section components (BusinessDetailsFields, OwnershipFields, etc.) that
// are called from both the operator-fills form and the send-intake form's
// optional pre-fill section.
export type FieldSetter<T> = <K extends keyof T>(key: K, value: T[K]) => void;
export type FeeOverridesForm = {
  achBatchFee: string;
  addressVerifFee: string;
  annualMembershipFee: string;
  appFee: string;
  chargebackFee: string;
  cloverPlatformFee: string;
  cloverSecurityFee: string;
  ddaRejectFee: string;
  earlyCancelFee: string;
  minProcessFee: string;
  monthlyEquipmentRentalFee: string;
  pciAnnualFee: string;
  pciConciergeMonthlyFee: string;
  pciNonComplianceFee: string;
  pciProgramCd: string;
  regProdMonthlyFee: string;
  retrievalFee: string;
  statementFee: string;
  transactionFee: string;
  voiceAuthFee: string;
  wirelessActivationFee: string;
  wirelessFee: string;
  duesAndAssessmentsFlg: string;
  passthruInterchgCostsFlg: string;
};

export const EMPTY_FEE_OVERRIDES: FeeOverridesForm = {
  achBatchFee: "",
  addressVerifFee: "",
  annualMembershipFee: "",
  appFee: "",
  chargebackFee: "",
  cloverPlatformFee: "",
  cloverSecurityFee: "",
  ddaRejectFee: "",
  earlyCancelFee: "",
  minProcessFee: "",
  monthlyEquipmentRentalFee: "",
  pciAnnualFee: "",
  pciConciergeMonthlyFee: "",
  pciNonComplianceFee: "",
  pciProgramCd: "",
  regProdMonthlyFee: "",
  retrievalFee: "",
  statementFee: "",
  transactionFee: "",
  voiceAuthFee: "",
  wirelessActivationFee: "",
  wirelessFee: "",
  duesAndAssessmentsFlg: "",
  passthruInterchgCostsFlg: "",
};
export type IcPlusPricingOverridesForm = {
  interchangeTypeCode: string;
  amexOptBlueQualDiscountPct: string;
  discoverQualCreditDiscountPct: string;
  mastercardQualCreditDiscountPct: string;
  visaQualCreditDiscountPct: string;
};

export const EMPTY_IC_PLUS_PRICING_OVERRIDES: IcPlusPricingOverridesForm = {
  interchangeTypeCode: "",
  amexOptBlueQualDiscountPct: "",
  discoverQualCreditDiscountPct: "",
  mastercardQualCreditDiscountPct: "",
  visaQualCreditDiscountPct: "",
};
export type FleetOverridesForm = {
  voyagerAuthFee: string;
  voyagerSalesDiscountPct: string;
  wexSalesDiscountPct: string;
  wexTransactionFee: string;
  wexChargebackFee: string;
  wexRetrievalFee: string;
  pinDebitDiscountFee: string;
  pinDebitTransactionFee: string;
  ebtFnsNumber: string;
  ebtTransactionFee: string;
};

export const EMPTY_FLEET_OVERRIDES: FleetOverridesForm = {
  voyagerAuthFee: "",
  voyagerSalesDiscountPct: "",
  wexSalesDiscountPct: "",
  wexTransactionFee: "",
  wexChargebackFee: "",
  wexRetrievalFee: "",
  pinDebitDiscountFee: "",
  pinDebitTransactionFee: "",
  ebtFnsNumber: "",
  ebtTransactionFee: "",
};
export type CloverSecurityAndTransarmorForm = {
  transarmorDataProtectionFlg: string;
  transarmorTokenTypeCd: string;
  transarmorMultiPayToken: string;
  transarmorMonthlyFee: string;
  cloverSecurityBundleCd: string;
  cloverSecurityPlusFee: string;
  pciComplianceServiceFee: string;
  pciComplianceServiceFeeFrequency: string;
  pciComplianceServiceFeeMonthToBill: string;
};

export const EMPTY_CLOVER_SECURITY_AND_TRANSARMOR: CloverSecurityAndTransarmorForm =
  {
    transarmorDataProtectionFlg: "",
    transarmorTokenTypeCd: "",
    transarmorMultiPayToken: "",
    transarmorMonthlyFee: "",
    cloverSecurityBundleCd: "",
    cloverSecurityPlusFee: "",
    pciComplianceServiceFee: "",
    pciComplianceServiceFeeFrequency: "",
    pciComplianceServiceFeeMonthToBill: "",
  };
